import AIRoomGame from "./AIRoomGame.tsx";
import AIRoomNameEntry from "./AIRoomNameEntry.tsx";
import AIRoomSettings from "./AIRoomSettings.tsx";
import AIRoomInstructions from "./AIRoomInstructions.tsx";
import AIRoomLobby from "./AIRoomLobby.tsx";
import AIRoomEmptyState from "./AIRoomEmptyState.tsx";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAIRoomSocket } from "./hooks/useAIRoomSocket.js";
import WaitingForPromptsRender from "./Components/WaitingForPromptsRender.js";

export type AIRoomState =
  | "name-entry"
  | "settings"
  | "instructions"
  | "lobby"
  | "empty"
  | "done";

interface PublicPlayerData {
  sessionId: string;
  name: string;
}

interface OwnerSettings {
  craziness: number;
  sexualScale: number;
  drinkAmount: number;
  customInstructions: string;
}

interface RoomStatePayload {
  players?: PublicPlayerData[];
  totalPlayers?: number;
  promptCount?: number;
  customPromptCount?: number;
  currentIndex?: number;
  setupDone?: boolean;
  isGenerating?: boolean;
  ownerSessionId?: string | null;
  ownerSetupDone?: boolean;
  ownerSetupStage?: "settings" | "instructions";
}

interface GenerateAck {
  ok?: boolean;
  promptCount?: number;
  reason?: "generating" | "error";
}

const DEFAULT_SETTINGS: OwnerSettings = {
  craziness: 1,
  sexualScale: 1,
  drinkAmount: 1,
  customInstructions: "",
};

interface AIRoomProps {
  roomName: string;
  setRoomState: (state: string) => void;
  AIRoomState: string;
  setAIRoomState: (state: string) => void;
  socket: any;
  containerHeight: number;
  showInfoMessage: (
    type: string,
    message: string,
    isAdd: boolean,
    duration?: string,
    isDelay?: boolean,
  ) => void;
  fetchString: string;
  sessionId: string;
}

function AIRoom({
  roomName,
  setRoomState,
  AIRoomState,
  setAIRoomState,
  socket,
  containerHeight,
  showInfoMessage,
  fetchString,
  sessionId,
}: AIRoomProps) {
  const { t } = useTranslation();
  const room = roomName.toLowerCase();

  const [waitingForPrompts, setWaitingForPrompts] = useState(false);
  const [players, setPlayers] = useState<PublicPlayerData[]>([]);
  const [promptCount, setPromptCount] = useState(0);
  const [customPromptCount, setCustomPromptCount] = useState(0);
  const [setupDone, setSetupDone] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [ownerSessionId, setOwnerSessionId] = useState<string | null>(null);
  // null until the first room state arrives
  const [ownerSetupDone, setOwnerSetupDone] = useState<boolean | null>(null);
  const [ownerSetupStage, setOwnerSetupStage] = useState<
    "settings" | "instructions"
  >("settings");
  const [ownerSettings, setOwnerSettings] = useState<OwnerSettings | null>(
    null,
  );
  // Set when START was pressed while the server was already generating; the
  // effect below enters the game as soon as prompts exist.
  const pendingStart = useRef(false);

  const isOwner = ownerSessionId === sessionId;

  const onRoomState = useCallback((data: RoomStatePayload) => {
    if (data.players) setPlayers(data.players);
    if (data.promptCount !== undefined) setPromptCount(data.promptCount);
    if (data.customPromptCount !== undefined)
      setCustomPromptCount(data.customPromptCount);
    if (data.setupDone !== undefined) setSetupDone(data.setupDone);
    if (data.isGenerating !== undefined) setIsGenerating(data.isGenerating);
    if (data.ownerSessionId !== undefined)
      setOwnerSessionId(data.ownerSessionId);
    if (data.ownerSetupDone !== undefined)
      setOwnerSetupDone(data.ownerSetupDone);
    if (data.ownerSetupStage !== undefined)
      setOwnerSetupStage(data.ownerSetupStage);
  }, []);

  const onOwnerState = useCallback((data: { settings?: OwnerSettings }) => {
    if (data && data.settings) setOwnerSettings(data.settings);
  }, []);

  const onKicked = useCallback(() => {
    socket.emit("leave room", room);
    setRoomState("start screen");
    setAIRoomState("name-entry");
    showInfoMessage("warning", t("ai.kicked"), false);
  }, [socket, room, setRoomState, setAIRoomState, showInfoMessage, t]);

  useAIRoomSocket(socket, room, sessionId, {
    setWaitingForPrompts,
    onRoomState,
    onOwnerState,
    onKicked,
  });

  // The creator must complete settings and instructions once before using
  // the room, also after a refresh or after leaving mid-way.
  useEffect(() => {
    if (isOwner && ownerSetupDone === false && AIRoomState === "lobby") {
      setAIRoomState(ownerSetupStage);
    }
  }, [isOwner, ownerSetupDone, ownerSetupStage, AIRoomState, setAIRoomState]);

  const setSetupStage = (stage: "settings" | "instructions") => {
    setOwnerSetupStage(stage);
    socket.emit("ai room owner setup stage", { roomName: room, sessionId, stage });
  };

  // TILBAKE on the first settings screen: an owner who has not finished setup
  // leaves the room (rejoining resumes at the saved stage); otherwise lobby.
  const leaveSettings = () => {
    if (ownerSetupDone === false) {
      socket.emit("leave room", room);
      setRoomState("start screen");
      setAIRoomState("name-entry");
      return;
    }
    setAIRoomState(settingsOrigin);
  };

  useEffect(() => {
    if (pendingStart.current && promptCount > 0) {
      pendingStart.current = false;
      setAIRoomState("done");
    }
  }, [promptCount, isGenerating, setAIRoomState]);

  const startGeneration = () => {
    socket.emit(
      "ai room generate",
      { roomName: room, sessionId },
      (res: GenerateAck | undefined) => {
        if (res && res.ok) {
          setAIRoomState("done");
          return;
        }
        if (res && res.reason === "generating") {
          pendingStart.current = true;
          return;
        }
        showInfoMessage("warning", t("ai.generateFailed"), false);
      },
    );
  };

  const handleStart = () => {
    if (promptCount + customPromptCount === 0) {
      setAIRoomState("empty");
      return;
    }
    if (promptCount === 0) {
      startGeneration();
      return;
    }
    setAIRoomState("done");
  };

  // The owner's settings pass is always settings, then instructions, then
  // lobby, both on first setup and when reopened from the lobby icon.
  // Which settings step to open on: 1 when coming back from instructions
  const [settingsStep, setSettingsStep] = useState(0);
  // Where settings were opened from, so TILBAKE and finishing return there
  const [settingsOrigin, setSettingsOrigin] = useState<"lobby" | "done">(
    "lobby",
  );
  const openSettingsFrom = (origin: "lobby" | "done") => () => {
    setSettingsOrigin(origin);
    setSettingsStep(0);
    setAIRoomState("settings");
  };

  const settings = ownerSettings ?? DEFAULT_SETTINGS;

  return (
    <>
      {waitingForPrompts && <WaitingForPromptsRender />}

      {AIRoomState === "name-entry" && (
        <AIRoomNameEntry
          roomName={room}
          sessionId={sessionId}
          socket={socket}
          setAIRoomState={(state: string) => {
            if (state === "settings") setSettingsStep(0);
            setAIRoomState(state);
          }}
          setRoomState={setRoomState}
          players={players}
          showInfoMessage={showInfoMessage}
        />
      )}

      {AIRoomState === "settings" && ownerSettings && (
        <AIRoomSettings
          roomName={room}
          sessionId={sessionId}
          socket={socket}
          initialSettings={settings}
          initialStep={settingsStep}
          onDone={() => {
            if (ownerSetupDone === false) setSetupStage("instructions");
            setAIRoomState("instructions");
          }}
          onBack={leaveSettings}
        />
      )}

      {AIRoomState === "instructions" && ownerSettings && (
        <AIRoomInstructions
          roomName={room}
          sessionId={sessionId}
          socket={socket}
          initialValue={settings.customInstructions}
          onDone={() => {
            if (ownerSetupDone === false) {
              // Optimistic: the server's broadcast confirms shortly after
              setOwnerSetupDone(true);
              setAIRoomState("lobby");
              return;
            }
            setAIRoomState(settingsOrigin);
          }}
          onBack={() => {
            if (ownerSetupDone === false) setSetupStage("settings");
            setSettingsStep(1);
            setAIRoomState("settings");
          }}
        />
      )}

      {AIRoomState === "lobby" && (
        <AIRoomLobby
          roomName={room}
          sessionId={sessionId}
          socket={socket}
          players={players}
          promptCount={promptCount}
          customPromptCount={customPromptCount}
          setupDone={setupDone}
          isGenerating={isGenerating}
          isOwner={isOwner}
          showInfoMessage={showInfoMessage}
          containerHeight={containerHeight}
          setRoomState={setRoomState}
          setAIRoomState={setAIRoomState}
          onOpenSettings={openSettingsFrom("lobby")}
          onStart={handleStart}
        />
      )}

      {AIRoomState === "empty" && (
        <AIRoomEmptyState
          roomName={room}
          isGenerating={isGenerating}
          setAIRoomState={setAIRoomState}
          onGenerate={startGeneration}
        />
      )}

      {AIRoomState === "done" && (
        <AIRoomGame
          roomName={room}
          socket={socket}
          setAIRoomState={setAIRoomState}
          containerHeight={containerHeight}
          fetchString={fetchString}
          isOwner={isOwner}
          onOpenSettings={openSettingsFrom("done")}
        />
      )}
    </>
  );
}

export default AIRoom;
